import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, OrgRole, UserRole } from './index.type';
import { getUserOrgInfo } from '@/lib/utils/get-user-role';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      orgId: null,
      orgUDSId: null, // Selise blocks data gateway org id
      sigOrgId: null, // SELISE Signature (ARC) OrganizationId
      orgSlug: null,
      userId: null,
      activeOrgRole: null,
      isAuthLoading: false,
      platformRole: null,
      sessionExpired: false,
      login: () =>
        set((state) => ({
          ...state,
          isAuthenticated: true,
          sessionExpired: false,
          // Reset any identity / org context left over from a previously
          // signed-in account. Without this, switching accounts keeps the old
          // `orgSlug`, and the post-login onboarding redirect sends the new user
          // into an org they don't belong to → /org/unauthorized. These are
          // repopulated from GET /iam/me after login.
          user: null,
          userId: null,
          platformRole: null,
          orgId: null,
          orgUDSId: null,
          sigOrgId: null,
          orgSlug: null,
          activeOrgRole: null,
        })),
      logout: () =>
        set({
          isAuthenticated: false,
          user: null,
          orgId: null,
          orgUDSId: null,
          sigOrgId: null,
          orgSlug: null,
          userId: null,
          activeOrgRole: null,
          sessionExpired: false,
        }),
      // Clears auth state like logout(), but flags the session as expired so the
      // login page can show a "your session expired" banner. Use this on refresh
      // token failure; use logout() for an intentional sign-out.
      expireSession: () =>
        set({
          isAuthenticated: false,
          user: null,
          orgId: null,
          orgUDSId: null,
          sigOrgId: null,
          orgSlug: null,
          userId: null,
          activeOrgRole: null,
          sessionExpired: true,
        }),
      setSessionExpired: (value) =>
        set((state) => ({
          ...state,
          sessionExpired: value,
        })),
      setUser: (user) => {
        const { userId, platformRole } = getUserOrgInfo(user);

        return set((state) => ({
          ...state,
          user,
          userId,
          platformRole: (platformRole as UserRole) || null,
        }));
      },
      setOrg: (orgId, orgSlug, orgUDSId, sigOrgId = null) => {
        set((state) => {
          const membership = state.user?.memberships?.find((m) => m.organizationId === orgId);
          const activeOrgRole = (membership?.roles?.[0] ?? null) as OrgRole | null;

          // Skip if nothing changed
          if (
            state.orgId === orgId &&
            state.orgSlug === orgSlug &&
            state.activeOrgRole === activeOrgRole &&
            state.orgUDSId === orgUDSId &&
            state.sigOrgId === sigOrgId
          ) {
            return state;
          }

          return {
            ...state,
            orgId,
            orgSlug,
            activeOrgRole,
            orgUDSId,
            sigOrgId,
          };
        });
      },
      clearOrg: () =>
        set((state) => ({
          ...state,
          orgId: null,
          orgSlug: null,
          activeOrgRole: null,
          sigOrgId: null,
        })),
      setAuthLoading: (isLoading) =>
        set((state) => ({
          ...state,
          isAuthLoading: isLoading,
        })),
    }),

    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        userId: state.userId,
        platformRole: state.platformRole,
        orgId: state.orgId,
        orgUDSId: state.orgUDSId,
        sigOrgId: state.sigOrgId,
        orgSlug: state.orgSlug,
        activeOrgRole: state.activeOrgRole,
        sessionExpired: state.sessionExpired,
      }),
    }
  )
);
